import {
  captionMinutesUsed,
  formatBytes,
  formatPlanPrice,
  INVOICE_TAX_NOTE,
  isPaidPlan,
  ORG_ROLES,
  type OrgPlan,
  type OrgRole,
  type PaidPlanId,
  PLANS,
  type PlanLimits,
  PUBLIC_PLANS,
  ROLE_LABELS,
  STORAGE_CATEGORY_LABELS,
  SUBSCRIPTION_STATUS_LABELS,
  TRIAL_DAYS,
} from "@cva/shared";
import * as Tabs from "@radix-ui/react-tabs";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  Bot,
  Check,
  Copy,
  CreditCard,
  Gauge,
  HardDrive,
  Mail,
  Trash2,
  UserMinus,
} from "lucide-react";
import { type FormEvent, type ReactNode, useEffect, useState } from "react";
import { toast } from "sonner";
import { Avatar } from "../components/app-shell";
import { Button } from "../components/ui/button";
import { Badge, Card, CardHeader, PageHeader, Progress, Skeleton } from "../components/ui/card";
import { ConfirmDialog } from "../components/ui/confirm";
import { Dialog, DialogContent, DialogFooter } from "../components/ui/dialog";
import { Field, Input, Select } from "../components/ui/input";
import { client, orpc } from "../lib/api";
import { authClient } from "../lib/auth-client";
import { choosePlan, openBillingPortal } from "../lib/billing";
import { useOrgId, useOrgRole } from "../lib/org";
import { useSession } from "../lib/session";
import { useSystemInfo } from "../lib/system";
import { cn, errorMessage, formatDate } from "../lib/utils";

const TABS = [
  { id: "general", label: "Allgemein" },
  { id: "members", label: "Mitglieder" },
  { id: "plan", label: "Tarif" },
  { id: "storage", label: "Speicher" },
  { id: "integrations", label: "KI & Integrationen" },
] as const;
type TabId = (typeof TABS)[number]["id"];

export function SettingsPage() {
  const orgId = useOrgId();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { tab?: TabId };
  const tab = search.tab ?? "general";
  return (
    <>
      <PageHeader title="Einstellungen" />
      <Tabs.Root
        value={tab}
        onValueChange={(value) =>
          void navigate({
            to: "/o/$orgId/settings",
            params: { orgId },
            search: { tab: value as TabId },
          })
        }
      >
        <Tabs.List className="mb-6 flex gap-1 border-b border-zinc-200">
          {TABS.map((item) => (
            <Tabs.Trigger
              key={item.id}
              value={item.id}
              className="-mb-px border-b-2 border-transparent px-3 py-2 text-sm font-medium text-zinc-500 hover:text-zinc-900 data-[state=active]:border-brand-600 data-[state=active]:text-brand-700"
            >
              {item.label}
            </Tabs.Trigger>
          ))}
        </Tabs.List>
        <Tabs.Content value="general">
          <GeneralSettings orgId={orgId} />
        </Tabs.Content>
        <Tabs.Content value="members">
          <MemberSettings orgId={orgId} />
        </Tabs.Content>
        <Tabs.Content value="plan">
          <PlanSettings orgId={orgId} />
        </Tabs.Content>
        <Tabs.Content value="storage">
          <StorageSettings orgId={orgId} />
        </Tabs.Content>
        <Tabs.Content value="integrations">
          <IntegrationSettings />
        </Tabs.Content>
      </Tabs.Root>
    </>
  );
}

function GeneralSettings({ orgId }: { orgId: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { can } = useOrgRole(orgId);
  const org = useQuery(orpc.orgs.get.queryOptions({ input: { orgId } }));
  const [name, setName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!name?.trim()) return;
    setBusy(true);
    const { error } = await authClient.organization.update({
      organizationId: orgId,
      data: { name: name.trim() },
    });
    setBusy(false);
    if (error) return toast.error(error.message ?? "Speichern fehlgeschlagen");
    await queryClient.invalidateQueries();
    setName(null);
    toast.success("Gespeichert");
  }

  if (!org.data) return <Skeleton className="h-48" />;
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Organisation"
          description={`Angelegt am ${formatDate(org.data.createdAt)}`}
        />
        <form onSubmit={save} className="space-y-4 p-5">
          <Field label="Name">
            <Input
              value={name ?? org.data.name}
              disabled={!can("org.update")}
              onChange={(e) => setName(e.target.value)}
              className="max-w-md"
            />
          </Field>
          <Field label="ID" hint="Wird z. B. von KI-Clients über MCP verwendet.">
            <Input value={org.data.id} readOnly className="max-w-md font-mono text-xs" />
          </Field>
          {can("org.update") ? (
            <Button type="submit" loading={busy} disabled={name === null}>
              Speichern
            </Button>
          ) : null}
        </form>
      </Card>
      {can("org.delete") ? (
        <Card className="border-red-200">
          <CardHeader
            title="Organisation löschen"
            description="Löscht alle Projekte, Dateien, Medien und Mitgliedschaften unwiderruflich."
            actions={
              <Button variant="danger" onClick={() => setDeleting(true)}>
                <Trash2 /> Löschen
              </Button>
            }
          />
        </Card>
      ) : null}
      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        danger
        title={`„${org.data.name}“ endgültig löschen?`}
        description="Alle Inhalte dieser Organisation werden unwiderruflich gelöscht."
        confirmLabel="Endgültig löschen"
        onConfirm={async () => {
          const { error } = await authClient.organization.delete({ organizationId: orgId });
          if (error) {
            return toast.error(
              error.code === "ORGANIZATION_HAS_ACTIVE_SUBSCRIPTION"
                ? "Die Organisation hat noch ein laufendes Abo. Kündige es zuerst unter Tarif → Abo & Rechnungen."
                : (error.message ?? "Löschen fehlgeschlagen"),
            );
          }
          await queryClient.invalidateQueries();
          await navigate({ to: "/" });
        }}
      />
    </div>
  );
}

function MemberSettings({ orgId }: { orgId: string }) {
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const { can, role: myRole } = useOrgRole(orgId);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<OrgRole>("editor");
  const [busy, setBusy] = useState(false);
  const manage = can("members.manage");
  const members = useQuery({
    queryKey: ["members", orgId],
    queryFn: async () => {
      const { data, error } = await authClient.organization.listMembers({
        query: { organizationId: orgId, limit: 500 },
      });
      if (error) throw error;
      return data.members;
    },
  });
  const invitations = useQuery({
    queryKey: ["invitations", orgId],
    queryFn: async () => {
      const { data, error } = await authClient.organization.listInvitations({
        query: { organizationId: orgId },
      });
      if (error) throw error;
      return data.filter((invitation) => invitation.status === "pending");
    },
    enabled: manage,
  });
  const plan = useQuery(orpc.plan.get.queryOptions({ input: { orgId } }));
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["members", orgId] });
    await queryClient.invalidateQueries({ queryKey: ["invitations", orgId] });
    await queryClient.invalidateQueries({ queryKey: orpc.orgs.key() });
    await queryClient.invalidateQueries({ queryKey: orpc.plan.key() });
  };
  const seats = plan.data?.limits.members ?? null;
  const taken = plan.data ? plan.data.usage.members + plan.data.usage.pendingInvitations : 0;

  async function invite(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    const { error } = await authClient.organization.inviteMember({
      email,
      role,
      organizationId: orgId,
    });
    setBusy(false);
    if (error) return toast.error(error.message ?? "Einladung fehlgeschlagen");
    setEmail("");
    toast.success(`Einladung an ${email} verschickt`);
    await refresh();
  }

  async function changeRole(memberId: string, newRole: OrgRole) {
    const { error } = await authClient.organization.updateMemberRole({
      memberId,
      role: newRole,
      organizationId: orgId,
    });
    if (error) return toast.error(error.message ?? "Rolle konnte nicht geändert werden");
    await refresh();
  }

  async function removeMember(memberId: string) {
    const { error } = await authClient.organization.removeMember({
      memberIdOrEmail: memberId,
      organizationId: orgId,
    });
    if (error) return toast.error(error.message ?? "Entfernen fehlgeschlagen");
    await refresh();
  }

  const assignable = ORG_ROLES.filter((item) => item !== "owner" || myRole === "owner");

  return (
    <div className="space-y-6">
      {manage ? (
        <Card>
          <CardHeader
            title="Person einladen"
            description={`Die Person bekommt eine E-Mail mit einem Einladungslink (7 Tage gültig).${
              plan.data && seats !== null
                ? ` Im Tarif ${PLANS[plan.data.plan].name} sind ${taken} von ${seats} Plätzen belegt, offene Einladungen zählen mit.`
                : ""
            }`}
          />
          <form onSubmit={invite} className="flex flex-wrap items-end gap-3 p-5">
            <div className="min-w-64 flex-1">
              <Field label="E-Mail">
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@firma.de"
                />
              </Field>
            </div>
            <Field label="Rolle">
              <Select
                value={role}
                onChange={(e) => setRole(e.target.value as OrgRole)}
                className="w-40"
              >
                {assignable.map((item) => (
                  <option key={item} value={item}>
                    {ROLE_LABELS[item]}
                  </option>
                ))}
              </Select>
            </Field>
            <Button type="submit" loading={busy}>
              <Mail /> Einladen
            </Button>
          </form>
        </Card>
      ) : null}

      <Card>
        <CardHeader
          title="Mitglieder"
          description="Owner verwalten alles; Admins zusätzlich Mitglieder; Editoren erstellen Inhalte; Betrachter sehen nur."
        />
        {members.isLoading ? (
          <Skeleton className="m-5 h-24" />
        ) : (
          <ul className="divide-y divide-zinc-100">
            {members.data?.map((member) => {
              const isMe = member.userId === session?.user.id;
              return (
                <li key={member.id} className="flex items-center gap-3 px-5 py-3">
                  <Avatar name={member.user.name} className="size-9 text-sm" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">
                      {member.user.name} {isMe ? <span className="text-zinc-400">(du)</span> : null}
                    </p>
                    <p className="truncate text-xs text-zinc-500">{member.user.email}</p>
                  </div>
                  {manage && !isMe && (member.role !== "owner" || myRole === "owner") ? (
                    <>
                      <Select
                        value={member.role}
                        onChange={(e) => changeRole(member.id, e.target.value as OrgRole)}
                        className="w-36"
                      >
                        {assignable.map((item) => (
                          <option key={item} value={item}>
                            {ROLE_LABELS[item]}
                          </option>
                        ))}
                      </Select>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Entfernen"
                        onClick={() => removeMember(member.id)}
                      >
                        <UserMinus />
                      </Button>
                    </>
                  ) : (
                    <Badge tone={member.role === "owner" ? "brand" : "neutral"}>
                      {ROLE_LABELS[member.role as OrgRole] ?? member.role}
                    </Badge>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {manage && invitations.data && invitations.data.length > 0 ? (
        <Card>
          <CardHeader title="Offene Einladungen" />
          <ul className="divide-y divide-zinc-100">
            {invitations.data.map((invitation) => (
              <li key={invitation.id} className="flex items-center gap-3 px-5 py-3 text-sm">
                <Mail className="size-4 text-zinc-400" />
                <span className="flex-1">{invitation.email}</span>
                <Badge>{ROLE_LABELS[invitation.role as OrgRole] ?? invitation.role}</Badge>
                <span className="text-xs text-zinc-500">
                  bis {formatDate(new Date(invitation.expiresAt))}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={async () => {
                    await authClient.organization.cancelInvitation({ invitationId: invitation.id });
                    await refresh();
                  }}
                >
                  Zurückziehen
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </div>
  );
}

function UsageRow({
  label,
  bytes,
  total,
  extra,
}: {
  label: ReactNode;
  bytes: number;
  total: number;
  extra?: ReactNode;
}) {
  return (
    <div className="px-5 py-3">
      <div className="mb-1.5 flex items-center justify-between gap-3 text-sm">
        <span className="min-w-0 truncate">{label}</span>
        <span className="shrink-0 text-zinc-600">
          {extra ? <span className="mr-2 text-xs text-zinc-400">{extra}</span> : null}
          {formatBytes(bytes)}
        </span>
      </div>
      <Progress value={total ? (bytes / total) * 100 : 0} className="h-1.5" />
    </div>
  );
}

function LimitRow({
  label,
  used,
  limit,
  format = String,
  extra,
}: {
  label: string;
  used: number;
  limit: number | null;
  format?: (value: number) => string;
  extra?: string | undefined;
}) {
  const percent = limit ? (used / limit) * 100 : 0;
  return (
    <div className="px-5 py-3">
      <div className="mb-1.5 flex items-center justify-between gap-3 text-sm">
        <span className="min-w-0 truncate">
          {label}
          {extra ? <span className="ml-2 text-xs text-zinc-400">{extra}</span> : null}
        </span>
        <span className="shrink-0 text-zinc-600 tabular-nums">
          {format(used)} von {limit === null ? "unbegrenzt" : format(limit)}
        </span>
      </div>
      {limit === null ? null : (
        <Progress
          value={Math.min(100, percent)}
          tone={percent >= 100 ? "danger" : percent > 80 ? "warning" : "brand"}
          className="h-1.5"
        />
      )}
    </div>
  );
}

const unlimited = (value: number | null, format: (value: number) => string = String) =>
  value === null ? "unbegrenzt" : format(value);

const COMPARISON: { label: string; value: (limits: PlanLimits) => string }[] = [
  {
    label: "Nutzer:innen",
    value: (l) => unlimited(l.members, (n) => (n === 1 ? "1 Person" : `bis ${n}`)),
  },
  { label: "Speicher", value: (l) => unlimited(l.storageBytes, (n) => formatBytes(n, 0)) },
  { label: "Untertitel pro Monat", value: (l) => unlimited(l.captionMinutes, (n) => `${n} Min.`) },
  { label: "Freistellungen pro Monat", value: (l) => unlimited(l.cutouts) },
  { label: "Aktive Designs", value: (l) => unlimited(l.documents) },
  {
    label: "Dateigröße pro Upload",
    value: (l) => (l.uploadBytes === null ? "Serverlimit" : formatBytes(l.uploadBytes)),
  },
  { label: "Exportseiten pro Monat", value: (l) => unlimited(l.exportPages) },
  {
    label: "Exportformate",
    value: (l) => (l.exportPages === null ? "PNG, JPG, PDF, MP4" : "PNG, JPG · max. 2.048 px"),
  },
  {
    label: "Video-Freistellung",
    value: (l) =>
      l.videoCutouts === 0 ? "Ab Solo" : unlimited(l.videoCutouts, (n) => `${n} / Monat`),
  },
  { label: "Brand Kits", value: (l) => unlimited(l.brandKits) },
  { label: "KI-Assistenten (MCP)", value: (l) => (l.mcp ? "✓" : "–") },
];

function PlanSettings({ orgId }: { orgId: string }) {
  const plan = useQuery(orpc.plan.get.queryOptions({ input: { orgId } }));
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { checkout?: string };
  const [busy, setBusy] = useState<string | null>(null);
  const [ordering, setOrdering] = useState<PaidPlanId | null>(null);

  // Back from Stripe's checkout: the plugin already synced the subscription.
  useEffect(() => {
    if (search.checkout !== "done") return;
    toast.success("Danke! Dein Abo ist eingerichtet.");
    void queryClient.invalidateQueries({ queryKey: orpc.plan.key() });
    void queryClient.invalidateQueries({ queryKey: orpc.orgs.key() });
    void navigate({
      to: "/o/$orgId/settings",
      params: { orgId },
      search: { tab: "plan" },
      replace: true,
    });
  }, [search.checkout, orgId, navigate, queryClient]);

  if (!plan.data) return <Skeleton className="h-64" />;
  const data = plan.data;
  const { billing } = data;
  const current = PLANS[data.plan];
  const lastDay = new Date(data.periodEnd.getTime() - 24 * 60 * 60 * 1000);
  const minutes = (value: number) => `${value} Min.`;
  const running = billing.enabled && billing.required && !billing.readOnly;
  const online = billing.enabled && billing.required;

  const run = async (key: string, action: () => Promise<void>) => {
    setBusy(key);
    try {
      await action();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : String(error));
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="p-5">
        <div className="flex items-start gap-4">
          <span className="flex size-11 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
            <Gauge className="size-5" />
          </span>
          <div className="flex-1">
            <p className="text-sm text-zinc-500">Aktueller Tarif</p>
            <p className="flex flex-wrap items-center gap-x-2 text-2xl font-semibold">
              {current.name}{" "}
              <span className="text-base font-normal text-zinc-400">
                {current.priceCents === null
                  ? "ohne Limits"
                  : `${formatPlanPrice(current)} im Monat`}
              </span>
              {online && billing.status ? (
                <Badge tone={statusTone(billing)}>
                  {SUBSCRIPTION_STATUS_LABELS[billing.status]}
                </Badge>
              ) : null}
            </p>
            {online ? (
              <SubscriptionLine billing={billing} price={formatPlanPrice(current)} />
            ) : null}
            <p className="mt-2 text-xs text-zinc-500">
              Untertitel, Freistellungen und Exportseiten zählen pro Kalendermonat, gerade vom{" "}
              {formatDate(data.periodStart)} bis {formatDate(lastDay)}. Am 1. beginnen sie neu.
            </p>
          </div>
          {billing.canManage && billing.status && billing.status !== "incomplete_expired" ? (
            <Button
              variant="secondary"
              disabled={busy !== null}
              onClick={() => void run("portal", () => openBillingPortal(orgId))}
            >
              <CreditCard /> {busy === "portal" ? "Öffne Stripe …" : "Abo & Rechnungen"}
            </Button>
          ) : null}
        </div>
      </Card>
      <Card>
        <CardHeader
          title="Nutzung"
          description="Liegt etwas über dem Limit, bleibt alles erhalten, nur Neues ist nicht mehr möglich."
        />
        <div className="divide-y divide-zinc-100">
          <LimitRow
            label="Speicher"
            used={data.usage.storageBytes}
            limit={data.limits.storageBytes}
            format={formatBytes}
            extra={data.storageOverride ? "eigenes Kontingent" : undefined}
          />
          <LimitRow
            label="Aktive Designs"
            used={data.usage.documents}
            limit={data.limits.documents}
          />
          <LimitRow
            label="Exportseiten diesen Monat"
            used={data.usage.exportPages}
            limit={data.limits.exportPages}
          />
          <LimitRow
            label="Untertitel diesen Monat"
            used={captionMinutesUsed(data.usage.captionSeconds)}
            limit={data.limits.captionMinutes}
            format={minutes}
          />
          <LimitRow
            label="Freistellungen diesen Monat"
            used={data.usage.cutouts}
            limit={data.limits.cutouts}
          />
          <LimitRow
            label="Mitglieder"
            used={data.usage.members + data.usage.pendingInvitations}
            limit={data.limits.members}
            extra={
              data.usage.pendingInvitations > 0
                ? `davon ${data.usage.pendingInvitations} ${data.usage.pendingInvitations === 1 ? "Einladung" : "Einladungen"} offen`
                : undefined
            }
          />
          <LimitRow label="Brand Kits" used={data.usage.brandKits} limit={data.limits.brandKits} />
        </div>
      </Card>
      <Card>
        <CardHeader
          title="Tarife"
          description={
            !billing.enabled
              ? "Die Online-Bezahlung kommt bald. Bis dahin stellen wir den Tarif auf Anfrage um."
              : data.plan === "intern"
                ? "Diese Organisation läuft intern und braucht kein Abo."
                : [
                    "Monatlich kündbar, Wechsel jederzeit (anteilig verrechnet).",
                    billing.trialAvailable
                      ? `Die ersten ${TRIAL_DAYS} Tage sind kostenlos, abgebucht wird erst danach.`
                      : null,
                    INVOICE_TAX_NOTE,
                    billing.canManage ? null : "Buchen und wechseln kann der Owner.",
                  ]
                    .filter(Boolean)
                    .join(" ")
          }
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-zinc-100 text-left">
              <tr>
                <th className="px-5 py-3 font-medium text-zinc-500" />
                {PUBLIC_PLANS.map((item) => (
                  <th
                    key={item.id}
                    className={cn(
                      "px-4 py-3 font-semibold",
                      item.id === data.plan && "bg-brand-50/60",
                    )}
                  >
                    <span className="flex flex-wrap items-center gap-2">
                      {item.name}
                      {item.id === data.plan &&
                      (running || !billing.enabled || data.plan === "free") ? (
                        <Badge tone="brand">Dein Tarif</Badge>
                      ) : null}
                    </span>
                    <span className="block text-xs font-normal text-zinc-500">
                      {formatPlanPrice(item)} im Monat
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 tabular-nums">
              {COMPARISON.map((row) => (
                <tr key={row.label}>
                  <td className="px-5 py-2.5 text-zinc-600">{row.label}</td>
                  {PUBLIC_PLANS.map((item) => (
                    <td
                      key={item.id}
                      className={cn("px-4 py-2.5", item.id === data.plan && "bg-brand-50/60")}
                    >
                      {row.value(item.limits)}
                    </td>
                  ))}
                </tr>
              ))}
              {billing.canManage ? (
                <tr>
                  <td className="px-5 py-3" />
                  {PUBLIC_PLANS.map((item) => {
                    if (!isPaidPlan(item.id))
                      return (
                        <td key={item.id} className="px-4 py-3 text-xs text-zinc-500">
                          {running ? "Nach Kündigung zum Abo-Ende" : "Dauerhaft kostenlos"}
                        </td>
                      );
                    const id = item.id;
                    const own = running && item.id === data.plan;
                    return (
                      <td
                        key={item.id}
                        className={cn("px-4 py-3", item.id === data.plan && "bg-brand-50/60")}
                      >
                        {own ? null : (
                          <Button
                            size="sm"
                            variant={running ? "secondary" : "primary"}
                            disabled={busy !== null}
                            onClick={() => setOrdering(id)}
                          >
                            {running
                              ? "Wechseln"
                              : billing.trialAvailable
                                ? `${TRIAL_DAYS} Tage testen`
                                : "Buchen"}
                          </Button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </Card>
      {ordering ? (
        <OrderDialog
          orgId={orgId}
          plan={ordering}
          change={running}
          trial={billing.trialAvailable && !running}
          onClose={() => setOrdering(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * Order summary right before Stripe (§ 312j BGB): plan, price, trial, term and
 * the button "Zahlungspflichtig bestellen". The confirmation with terms and
 * withdrawal notice is stored; the checkout is refused without it.
 */
function OrderDialog({
  orgId,
  plan,
  change,
  trial,
  onClose,
}: {
  orgId: string;
  plan: PaidPlanId;
  change: boolean;
  trial: boolean;
  onClose: () => void;
}) {
  const item = PLANS[plan];
  const orgs = useQuery(orpc.orgs.list.queryOptions());
  const orgName = orgs.data?.find((org) => org.id === orgId)?.name ?? "";
  const [terms, setTerms] = useState(false);
  const [earlyStart, setEarlyStart] = useState(false);
  const [busy, setBusy] = useState(false);
  const firstCharge = new Date(Date.now() + TRIAL_DAYS * 24 * 60 * 60 * 1000);
  const price = formatPlanPrice(item);
  const limits = item.limits;

  const order = async () => {
    setBusy(true);
    try {
      await client.billing.consent({ orgId, plan, acceptTerms: true, earlyStart: true });
      await choosePlan(orgId, plan);
    } catch (error) {
      toast.error(errorMessage(error));
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent
        title={change ? `Wechsel in den Tarif ${item.name}` : `Tarif ${item.name} bestellen`}
        description="Bitte prüfe deine Bestellung."
        className="max-w-lg"
      >
        <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 rounded-lg bg-zinc-50 p-4 text-sm">
          <dt className="text-zinc-500">Organisation</dt>
          <dd className="font-medium">{orgName}</dd>
          <dt className="text-zinc-500">Tarif</dt>
          <dd>
            <span className="font-medium">{item.name}</span>
            <span className="block text-zinc-600">
              {limits.members === 1 ? "1 Person" : `bis ${limits.members} Personen`},{" "}
              {limits.storageBytes === null
                ? "Speicher nach Absprache"
                : formatBytes(limits.storageBytes, 0)}{" "}
              Speicher, {limits.captionMinutes ?? "unbegrenzt"} Untertitel-Minuten und{" "}
              {limits.cutouts ?? "unbegrenzt"} Freistellungen im Monat,{" "}
              {limits.brandKits ?? "unbegrenzt"} Brand Kits, KI-Assistenten (MCP)
            </span>
          </dd>
          <dt className="text-zinc-500">Preis</dt>
          <dd>
            <span className="font-medium">{price} im Monat</span>
            <span className="block text-zinc-600">Endpreis. {INVOICE_TAX_NOTE}</span>
          </dd>
          {change ? (
            <>
              <dt className="text-zinc-500">Abrechnung</dt>
              <dd>Ab sofort; der Unterschied zum bisherigen Tarif wird anteilig verrechnet.</dd>
            </>
          ) : (
            <>
              <dt className="text-zinc-500">Testphase</dt>
              <dd>
                {trial
                  ? `${TRIAL_DAYS} Tage kostenlos. Erste Abbuchung am ${formatDate(firstCharge)}, wenn du nicht vorher kündigst.`
                  : "Keine (die Testphase wurde für diese Organisation schon genutzt). Erste Abbuchung sofort."}
              </dd>
            </>
          )}
          <dt className="text-zinc-500">Laufzeit</dt>
          <dd>Ein Monat, verlängert sich monatlich. Jederzeit zum Monatsende kündbar.</dd>
          <dt className="text-zinc-500">Zahlung</dt>
          <dd>Im nächsten Schritt hinterlegst du bei Stripe dein Zahlungsmittel.</dd>
        </dl>
        <div className="mt-4 space-y-3 text-sm">
          <label className="flex gap-2.5">
            <input
              type="checkbox"
              className="mt-0.5 size-4 shrink-0 accent-brand-600"
              checked={terms}
              onChange={(event) => setTerms(event.target.checked)}
            />
            <span>
              Ich stimme den{" "}
              <a href="/nutzungsbedingungen" target="_blank" rel="noreferrer" className="underline">
                AGB
              </a>{" "}
              zu und habe die{" "}
              <a href="/widerruf" target="_blank" rel="noreferrer" className="underline">
                Widerrufsbelehrung
              </a>{" "}
              zur Kenntnis genommen.
            </span>
          </label>
          <label className="flex gap-2.5">
            <input
              type="checkbox"
              className="mt-0.5 size-4 shrink-0 accent-brand-600"
              checked={earlyStart}
              onChange={(event) => setEarlyStart(event.target.checked)}
            />
            <span>
              Ich verlange ausdrücklich, dass ihr sofort, also vor Ablauf der Widerrufsfrist, mit
              der Leistung beginnt. Mir ist bekannt, dass ich bei einem Widerruf für bereits
              erbrachte kostenpflichtige Leistungen anteiligen Wertersatz zahle.
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button onClick={() => void order()} disabled={!terms || !earlyStart} loading={busy}>
            {change ? "Kostenpflichtig wechseln" : "Zahlungspflichtig bestellen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function statusTone(billing: OrgPlan["billing"]) {
  if (billing.readOnly) return "danger" as const;
  if (billing.status === "past_due" || billing.endsAt) return "warning" as const;
  return billing.status === "trialing" ? ("brand" as const) : ("success" as const);
}

/** One sentence about the subscription: trial, next payment, end or why it is locked. */
function SubscriptionLine({ billing, price }: { billing: OrgPlan["billing"]; price: string }) {
  let text: string;
  if (billing.readOnly) {
    text =
      billing.status === null || billing.status === "incomplete_expired"
        ? "Noch kein Abo gebucht. Bis dahin lässt sich alles ansehen, aber nichts bearbeiten, hochladen oder exportieren."
        : "Das Abo ist beendet. Alles bleibt erhalten und lässt sich ansehen, bearbeiten und exportieren geht erst wieder mit einem Tarif.";
  } else if (billing.endsAt) {
    text = `Gekündigt, läuft bis ${formatDate(billing.endsAt)}. Danach ist die Organisation schreibgeschützt.`;
  } else if (billing.status === "past_due") {
    text =
      "Die letzte Abbuchung ist fehlgeschlagen, Stripe versucht es erneut. Bitte prüfe das Zahlungsmittel unter „Abo & Rechnungen“.";
  } else if (billing.status === "trialing" && billing.trialEnd) {
    text = `Kostenlos testen bis ${formatDate(billing.trialEnd)}, danach ${price} im Monat.`;
  } else {
    text = billing.periodEnd
      ? `Nächste Abbuchung am ${formatDate(billing.periodEnd)}.`
      : "Das Abo läuft.";
  }
  return (
    <p className={cn("mt-2 text-sm", billing.readOnly ? "text-red-700" : "text-zinc-600")}>
      {text}
    </p>
  );
}

function StorageSettings({ orgId }: { orgId: string }) {
  const usage = useQuery(orpc.storage.usage.queryOptions({ input: { orgId } }));
  if (!usage.data) return <Skeleton className="h-64" />;
  const data = usage.data;
  const percent = (data.totalBytes / Math.max(data.quotaBytes, 1)) * 100;
  return (
    <div className="space-y-6">
      <Card className="p-5">
        <div className="flex items-start gap-4">
          <span className="flex size-11 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
            <HardDrive className="size-5" />
          </span>
          <div className="flex-1">
            <p className="text-sm text-zinc-500">Belegter Speicher</p>
            <p className="text-2xl font-semibold">
              {formatBytes(data.totalBytes)}{" "}
              <span className="text-base font-normal text-zinc-400">
                von {formatBytes(data.quotaBytes)}
              </span>
            </p>
            <Progress
              value={percent}
              tone={percent > 90 ? "danger" : percent > 75 ? "warning" : "brand"}
              className="mt-3"
            />
            {data.trashBytes > 0 ? (
              <p className="mt-2 text-xs text-zinc-500">
                Davon {formatBytes(data.trashBytes)} im Papierkorb
              </p>
            ) : null}
          </div>
        </div>
      </Card>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title="Nach Dateityp" />
          <div className="divide-y divide-zinc-100">
            {data.byCategory.map((row) => (
              <UsageRow
                key={row.category}
                label={STORAGE_CATEGORY_LABELS[row.category]}
                bytes={row.bytes}
                total={data.totalBytes}
                extra={`${row.files} ${row.files === 1 ? "Datei" : "Dateien"}`}
              />
            ))}
            {data.byCategory.length === 0 ? (
              <p className="px-5 py-4 text-sm text-zinc-500">Noch nichts gespeichert.</p>
            ) : null}
          </div>
        </Card>
        <Card>
          <CardHeader
            title="Nach Person"
            description="Wer hat die Dateien hochgeladen bzw. erstellt?"
          />
          <div className="divide-y divide-zinc-100">
            {data.byUser.map((row) => (
              <UsageRow
                key={row.userId ?? "none"}
                label={
                  <span className="flex items-center gap-2">
                    <Avatar name={row.name} className="size-6 text-[10px]" />
                    {row.name}
                  </span>
                }
                bytes={row.bytes}
                total={data.totalBytes}
              />
            ))}
          </div>
        </Card>
        <Card>
          <CardHeader title="Nach Projekt" />
          <div className="divide-y divide-zinc-100">
            {data.byProject.map((row) => (
              <UsageRow
                key={row.projectId ?? "none"}
                label={row.name}
                bytes={row.bytes}
                total={data.totalBytes}
              />
            ))}
          </div>
        </Card>
        <Card>
          <CardHeader title="Größte Medien" />
          <ul className="divide-y divide-zinc-100">
            {data.largestAssets.map((asset) => (
              <li
                key={asset.id}
                className="flex items-center justify-between gap-3 px-5 py-3 text-sm"
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium">{asset.name}</span>
                  <span className="block text-xs text-zinc-500">
                    {asset.createdBy?.name ?? "Unbekannt"}
                  </span>
                </span>
                <span className="shrink-0 text-zinc-600">{formatBytes(asset.bytes)}</span>
              </li>
            ))}
            {data.largestAssets.length === 0 ? (
              <p className="px-5 py-4 text-sm text-zinc-500">Noch keine Medien.</p>
            ) : null}
          </ul>
        </Card>
      </div>
    </div>
  );
}

function CopyField({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex max-w-xl gap-2">
      <Input readOnly value={value} className="font-mono text-xs" />
      <Button
        variant="secondary"
        onClick={async () => {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
      >
        {copied ? <Check /> : <Copy />} {copied ? "Kopiert" : "Kopieren"}
      </Button>
    </div>
  );
}

function IntegrationSettings() {
  const { data: system } = useSystemInfo();
  if (!system) return <Skeleton className="h-48" />;
  const steps = [
    {
      name: "ChatGPT",
      text: "Einstellungen → Apps & Connectors → Erweitert: Entwicklermodus aktivieren → „Connector erstellen“ → URL einfügen, Authentifizierung „OAuth“.",
    },
    {
      name: "Claude",
      text: "Einstellungen → Connectors → „Benutzerdefinierten Connector hinzufügen“ → URL einfügen.",
    },
    {
      name: "Andere MCP-Clients",
      text: "Streamable HTTP mit OAuth 2.1 (Dynamic Client Registration, PKCE). Discovery unter /.well-known/oauth-protected-resource.",
    },
  ];
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="MCP-Server"
          description="Verbinde ChatGPT, Claude oder andere KI-Assistenten. Sie arbeiten dann mit deinen Rechten in deinen Organisationen."
        />
        <div className="space-y-5 p-5">
          <Field label="Server-URL">
            <CopyField value={system.mcpUrl} />
          </Field>
          <ol className="space-y-3">
            {steps.map((step, index) => (
              <li key={step.name} className="flex gap-3 text-sm">
                <span
                  className={cn(
                    "flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-50 text-xs font-semibold text-brand-700",
                  )}
                >
                  {index + 1}
                </span>
                <span>
                  <strong className="font-medium">{step.name}:</strong>{" "}
                  <span className="text-zinc-600">{step.text}</span>
                </span>
              </li>
            ))}
          </ol>
          <p className="flex items-start gap-2 rounded-lg bg-zinc-50 px-3 py-2.5 text-sm text-zinc-600">
            <Bot className="mt-0.5 size-4 shrink-0 text-brand-600" />
            Beim ersten Verbinden meldest du dich hier an und bestätigst den Zugriff. Verbundene
            Apps findest und widerrufst du unter „Konto“.
          </p>
        </div>
      </Card>
    </div>
  );
}
